import { Head } from '~/components/shared/Head';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../shared/Logo';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  VERIFY_MAGIC_LINK,
  VERIFY_EMAIL,
  RESEND_VERIFICATION_EMAIL,
} from '~/services/auth';
import { publicClient } from '~/services';
import { useSession } from '../contexts/SessionProvider';

interface VerifyEmailProps {
  type: 'magic_link' | 'email_verification';
}

function VerifyEmail({ type = 'magic_link' }: VerifyEmailProps) {
  const navigate = useNavigate();
  const { createSession } = useSession();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [t] = useTranslation('app');

  const verificationMutation =
    type === 'magic_link' ? VERIFY_MAGIC_LINK : VERIFY_EMAIL;

  const [verifyToken, { loading, error }] = useLazyQuery(verificationMutation, {
    client: publicClient,
    onCompleted: (data) => {
      if (data.response.hasError) {
        alert(t('token_expired'));
        navigate('/');
        return;
      }

      // Always create session and redirect to home for both cases
      createSession(data);
      navigate('/');
    },
    onError: () => {
      alert(t('verification_error'));
      navigate('/');
    },
    fetchPolicy: 'network-only',
  });

  const [emailInput, setEmailInput] = useState('');
  const [resend, { loading: resendLoading, data: resendData }] = useMutation(
    RESEND_VERIFICATION_EMAIL,
    { client: publicClient },
  );

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    // Execute the verification query
    verifyToken({
      variables: {
        data: {
          token,
        },
      },
    });
  }, [token, verifyToken, navigate]);

  const getTitle = () => {
    return type === 'magic_link'
      ? t('verify_magic_link.title')
      : t('verify_email.title');
  };

  const getMessage = () => {
    return type === 'magic_link'
      ? t('loading_verify')
      : t('loading_verify_email');
  };

  if (error) {
    return (
      <>
        <Head title={getTitle()} />
        <div className="bg-opacity- hero min-h-screen overflow-hidden bg-gradient-to-b">
          <div className="hero-content text-center">
            <div className="card bg-base-100 flex w-96 flex-col items-center gap-8 p-4 shadow-xl">
              <Logo className="w-32" />
              <div className="alert alert-error w-full">
                <span>{t('verification_error')}</span>
              </div>
              <div className="w-full space-y-2">
                <input
                  type="email"
                  placeholder={t('login:placeholder_email')}
                  className="input input-bordered w-full"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
                <button
                  className="btn btn-primary w-full rounded-full"
                  onClick={async () => {
                    if (!emailInput) return;
                    try {
                      await resend({
                        variables: { data: { email: emailInput } },
                      });
                    } catch (e) {}
                  }}
                  disabled={resendLoading}
                >
                  {resendLoading
                    ? t('login:sending')
                    : t('login:resend_verification_email')}
                </button>
                {resendData?.response?.message && (
                  <div className="text-base-content/60 text-xs">
                    {resendData.response.message}
                  </div>
                )}
                <button
                  className="btn btn-ghost w-full rounded-full"
                  onClick={() => navigate('/')}
                >
                  {t('back_to_login')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head title={getTitle()} />
      <div className="bg-opacity- hero min-h-screen overflow-hidden bg-gradient-to-b">
        <div className="hero-content text-center">
          <div className="card bg-base-100 flex w-96 flex-col items-center gap-8 p-4 shadow-xl">
            <Logo className="w-32" />
            <div className="mt-4 grid gap-2">{getMessage()}</div>
            {loading && (
              <progress className="progress progress-primary w-56"></progress>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default VerifyEmail;
